import { cn } from '../../lib/utils';

export type LegendDotGap = 4 | 8;

export interface LegendDotItemProps {
  /** Solid CSS color (hex/rgb/hsl) for the 8×8 rounded dot. */
  dotColor: string;
  /** Outfit Regular 13-pt label rendered next to the dot. */
  label: string;
  /**
   * Gap between dot and label. `4` (`gap-1`) is the canonical legend density
   * inside table-row chrome; `8` (`gap-2`) is the wider widget-header density.
   * Defaults to `4`.
   */
  gap?: LegendDotGap;
  className?: string;
}

const GAP_STYLES = {
  4: 'gap-1',
  8: 'gap-2',
} as const satisfies Readonly<Record<LegendDotGap, string>>;

/**
 * `LegendDotItem` — 8-px rounded dot + 13-pt Outfit Regular label, 4|8-px
 * gap. Per TDEV-273 Phase 7. Used inside donut/legend rows and stacked-bar
 * legends where a dot-style chip is preferred over a 24×8 swatch.
 */
export function LegendDotItem({ dotColor, label, gap = 4, className }: LegendDotItemProps) {
  return (
    <span className={cn('inline-flex items-center', GAP_STYLES[gap], className)}>
      <span
        data-testid="legend-dot"
        className="inline-block w-2 h-2 rounded-[2px] shrink-0"
        style={{ backgroundColor: dotColor }}
        aria-hidden="true"
      />
      <span className="text-p1 text-dawn-black">{label}</span>
    </span>
  );
}

LegendDotItem.displayName = 'LegendDotItem';
